import styles from './notification-carousel.module.scss';
import React from "react";

import type { Notification } from "../../services/api";

export interface NotificationCarouselProps {
  name?: string;
  classes?: string;
  /** Avisos a rotar (el texto va en `text`). */
  notifications: Notification[];
  /** Duración por aviso en ms si el aviso no trae `time` (default 4000). */
  interval?: number;
}

interface State {
  index: number;
  /** Visible=true → el aviso está en opacidad 1. */
  visible: boolean;
}

/** Duración del fundido (fade-out / fade-in) en ms. */
const FADE_MS = 350;

/**
 * Carrusel de avisos cortos. Muestra **un** aviso a la vez con un ciclo
 * fade-out → cambio → fade-in (sin solape de textos), respetando el `time`
 * de cada aviso.
 */
export default class NotificationCarousel extends React.Component<
  NotificationCarouselProps,
  State
> {
  static jsClass = "NotificationCarousel";
  static defaultProps: Partial<NotificationCarouselProps> = {
    name: "mbNotificationCarousel",
    interval: 4000,
  };

  state: State = { index: 0, visible: true };

  private holdTimer?: ReturnType<typeof setTimeout>;
  private fadeTimer?: ReturnType<typeof setTimeout>;

  componentDidMount() {
    this.schedule();
  }

  componentDidUpdate(prev: NotificationCarouselProps) {
    if (prev.notifications !== this.props.notifications) {
      this.clearTimers();
      this.setState({ index: 0, visible: true }, () => this.schedule());
    }
  }

  componentWillUnmount() {
    this.clearTimers();
  }

  private clearTimers() {
    if (this.holdTimer) clearTimeout(this.holdTimer);
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    this.holdTimer = undefined;
    this.fadeTimer = undefined;
  }

  private schedule() {
    this.clearTimers();
    const items = this.props.notifications;
    if (!items || items.length < 2) return;
    const time = items[this.state.index]?.time || this.props.interval || 4000;
    this.holdTimer = setTimeout(() => this.advance(), time);
  }

  private advance() {
    const items = this.props.notifications;
    // 1) funde a 0, 2) cambia el texto, 3) funde a 1 (sin solape).
    this.setState({ visible: false });
    this.fadeTimer = setTimeout(() => {
      this.setState(
        (s) => ({ index: (s.index + 1) % items.length, visible: true }),
        () => this.schedule()
      );
    }, FADE_MS);
  }

  render(): React.ReactNode {
    const { classes, notifications } = this.props;
    const { index, visible } = this.state;
    if (!notifications || notifications.length === 0) return null;

    const item = notifications[index % notifications.length];

    return (
      <div
        className={styles['mb-notif'] + (classes ? ' ' + classes : '')}
        aria-live="polite">
        <span
          key={item.slug || index}
          className={
            styles['mb-notif__item'] +
            (visible ? ' ' + styles['mb-notif__item--active'] : '')
          }>
          {item.text}
        </span>
      </div>
    );
  }
}
